<template>
  <tr
    v-show="!item.meta.isHide"
    class="apply_item"
    :class="{
      deleted: item.deleted,
      checked: item.meta.isChecked,
      'edit-mode': isEditMode
    }"
  >
    <td class="data__page align-left" ref="tableDataPageContentWidth" :class="{disabled: !item.meta.canViewReport}" :style="{ width: colgroup[0] }" @click="onViewApplyClick">
      <i v-if="isEditMode" class="icon icon--drag"></i>
      <div v-if="isEditMode" class="item__handle--wrap">
        <input
          type="checkbox"
          class="icon--dashboard-chkbox"
          :checked="item.meta.isChecked"
          @change="onApplyMetaChange('isChecked', $event.target.checked)"
        >
      </div>
      <p v-if="!isEditMode" class="page-name ellipsis">
        <i v-if="item.bRealtimePV" class="icon icon--real-time-logo"></i>
        <i v-if="item.abCount" class="icon icon--ab-test-exist"></i>
        <i v-if="item.shared" class="icon icon--share-send"></i>
        <i v-if="isSharedCategory" class="icon icon--share-receive"></i>
        <span v-if="item.meta.canViewReport"
              @click.stop="onViewApplyClick">
          <text-highlight :searchKeyword="searchKeyword" :textItem="item.pageName"></text-highlight>
        </span>
        <span v-else>
          <text-highlight :searchKeyword="searchKeyword" :textItem="item.pageName"></text-highlight>
        </span>
      </p>
      <p class="page-name" v-else>
        <input
          type="text"
          maxlength="50"
          :value="item.pageName"
          @input="onApplyChange('pageName', $event.target.value.trim())"
        >
      </p>
      <p class="page-url ellipsis">
        <span class="match-type">{{ item.matchType | matchType(true) }}</span>
        <span :title="item.url">{{ item.url }}</span>
      </p>

      <div v-if="item.deleted" class="deleted__layer" :style="{ width: tableWidth + 'px' }">
        <div class="content--wrap">
          <i class="icon icon--limit"></i>
          <span>{{ $t('common.deleted') }}</span>
          <button @click.stop="onCancelDelete">
            <i class="icon icon--undo"></i>
            <span>{{ $t('common.undo') }}</span>
          </button>
        </div>
      </div>
    </td>

    <td v-if="!isEditMode" class="data__setting" :style="{ width: colgroup[1] }" @click.stop>
      <i class="icon icon--dashboard-setting button__setting"
         :class="{disabled: (isSharedCategory || isDemo)}"
         @click="showSettingOption($event)"></i>
      <div class="setting__option--wrap" style="display: none">
        <ul>
          <li @click="goEditPage(item)">{{ $t('common.edit') }}</li>
          <li @click="showShareRow()">{{ $t('common.share') }}</li>
        </ul>
      </div>
    </td>

    <td class="data__pv align-right" :style="{ width: isEditMode ? colgroup[2] : colgroup[3] }">{{ item.pvPerMonth | thousand }}</td>

    <td
      class="data__status"
      :class="item.meta.uiState"
      :style="{ width: !isEditMode ? colgroup[4] : colgroup[5] }"
    >
      <div v-if="isEditMode && item.meta.requestableStates">
        <ui-selectbox
          :value="item.state"
          :width="126"
          :items="item.meta.requestableStates"
          @input="onApplyChange('state', $event)"
        />
      </div>
      <div v-else>{{ $t(`page.state.${item.meta.uiState}`) }}</div>
    </td>

    <td class="data__tracking-code" :style="{ width: $i18n.locale === 'ja' ? colgroup[6] : colgroup[7] }">
      <i v-if="item.meta.trackingCodeChecked"
         class="icon icon--tracking-code-check"></i>
      <ui-button v-else
        size="small"
        :disabled="isDemo || isEditMode || item.meta.trackingCodeChecked === null || isSharedCategory || item.state === 'stop'"
        @click.stop="$emit('onCheckInstallClick', item)">{{ $t('common.trackingCode.checkInstall') }}</ui-button>
    </td>

    <td
      class="data__start-date"
      :class="{'read-only': isEditMode && !item.meta.canChangeStartDate}"
      :style="{ width: !isEditMode ? colgroup[8] : colgroup[9] }"
    >
      <div v-if="isEditMode"
           class="date_picker--wrap"
           style="position: relative;"
           @click.stop="showDatePicker('start')"
      >
        <span class="selectedDate">
          <template v-if="item.meta.canChangeStartDate && !item.startDate">
            {{ getToday() | dashDate }}
          </template>
          <template v-else>
            {{ computedStartDate | dashDate }}
          </template>
        </span>
        <date-picker
          v-if="item.meta.showStartDatePicker"
          :isShowTimezone="true"
          :timezone="item.timezone"
          :timezoneName="item.timezoneName"
          :value="computedStartDate"
          :minDate="minStartDate"
          :maxDate="maxStartDate"
          :isFromNow="item.startDate === null"
          @input="onApplyChange('startDate', $event)"
          @onCloseClick="item.meta.showStartDatePicker = false"/>
      </div>
      <template class="selectedDate" v-else>{{ computedStartDate }}</template>
    </td>

    <td
      class="data__end-date"
      :class="{'read-only': isEditMode && !item.meta.canChangeEndDate}"
      :style="{ width: !isEditMode ? colgroup[10] : colgroup[11] }"
    >
      <div
        v-if="isEditMode"
        style="position: relative;"
        @click.stop="showDatePicker('end')"
      >
        <span class="selectedDate" v-if="item.endDate">{{ computedEndDate | dashDate($t('common.nonStop')) }}</span>
        <span class="selectedDate" v-else>{{ $t('common.nonStop') }}</span>
        <date-picker
          v-if="item.meta.showEndDatePicker"
          :isShowTimezone="true"
          :timezone="item.timezone"
          :timezoneName="item.timezoneName"
          :value="computedEndDate"
          :minDate="minEndDate"
          :isEndless="item.endDate === null"
          @input="onApplyChange('endDate', $event)"
          @onCloseClick="item.meta.showEndDatePicker = false"/>
      </div>
      <template v-else class="selectedDate">{{ computedEndDate | dashDate($t('common.nonStop')) }}</template>
    </td>

    <td class="data__weekly-report" :style="{ width: !isEditMode ? colgroup[12] : colgroup[13] }" @click.stop>
      <ui-switch
        :on="item.isSubscribeWeeklyReport && item.meta.canChangeWklyReport !== null && isWeeklyReportAvailable"
        :disabled="!item.meta.canChangeWklyReport || !isWeeklyReportAvailable || isSharedCategory || isDemo"
        @click="toggleSubscribeWeeklyReport"/>
    </td>

    <td v-if="!isEditMode" :style="{ width: colgroup[14] }" class="data__confirm">
      <ui-button
        iconName="icon--dashboard-confirm"
        size="small"
        :disabled="!item.meta.canViewReport"
        @click.stop="onViewApplyClick">
        {{ $t('common.view') }}
      </ui-button>
    </td>

    <td v-if="isEditMode" :style="{ width: colgroup[15] }" class="data__delete__page">
      <button v-show="!item.deleted" @click.stop="onDeleteClick" class="icon icon--trash"></button>
    </td>
  </tr>
</template>

<script>
import { parse, format, addDays, startOfDay, isSameDay, endOfDay } from 'date-fns'
import { triggerClickEvent, utcToLocalTimezone } from '@/libs/utils'
import { create } from 'vue-modal-dialogs'
import UiSelectbox from '@/components/ui/UiSelectbox'
import UiButton from '@/components/ui/UiButton'
import UiSwitch from '@/components/ui/UiSwitch'
import DatePicker from '@/components/date-picker'
import AppDialog from '@/components/ui/AppDialog'
import TextHighlight from '@/components/ui/TextHighlight'
import { mapState } from 'vuex'

const openDialog = create(AppDialog)

export default {
  components: {
    UiSelectbox,
    UiButton,
    UiSwitch,
    DatePicker,
    TextHighlight
  },
  props: [
    'item',
    'isEditMode',
    'isSharedCategory',
    'selectedApplies',
    'isWeeklyReportAvailable',
    'searchKeyword'
  ],
  data () {
    return {
      tableWidth: 0,
      checkingTrackingCodeInstallation: false,
      installCheckerApplyData: null,
      colgroup: [
        '*',
        '44px',
        '50px',
        '78px',
        '100px',
        '140px',
        '100px',
        '90px',
        '90px',
        '110px',
        '90px',
        '110px',
        '90px',
        '70px',
        '95px',
        '40px'
      ]
    }
  },
  computed: {
    ...mapState([
      'isABPlan',
      'isDemo'
    ]),
    computedStartDate () {
      let startDate = this.item.startDate || this.item.regDate
      if (typeof startDate === 'string') {
        return format(utcToLocalTimezone(startDate, this.item.timezoneName), 'YYYY-MM-DD')
      }
      return format(startDate, 'YYYY-MM-DD')
    },
    computedEndDate () {
      if (this.item.endDate) {
        if (typeof this.item.endDate === 'string') {
          return format(utcToLocalTimezone(this.item.endDate, this.item.timezoneName), 'YYYY-MM-DD')
        }
        return format(this.item.endDate, 'YYYY-MM-DD')
      } else {
        return null
      }
    },
    minStartDate () {
      return utcToLocalTimezone(addDays(new Date(), 1), this.item.timezoneName)
    },
    maxStartDate () {
      if (this.item.endDate) {
        const endDate = utcToLocalTimezone(parse(this.item.endDate), this.item.timezoneName)
        let date

        if (isSameDay(endDate, utcToLocalTimezone(new Date(), this.item.timezoneName))) {
          date = addDays(endDate, 1)
        } else {
          date = endDate
        }
        return date
      }
      return null
    },
    minEndDate () {
      const today = new Date()
      if (this.item.startDate) {
        const startDate = new Date(this.item.startDate)
        return utcToLocalTimezone(Math.max(today, startDate), this.item.timezoneName)
      }
      return utcToLocalTimezone(today, this.item.timezoneName)
    },
    timezoneStr () {
      return `(${this.item.timezone}) ${this.item.timezoneName}`
    }
  },
  watch: {
    'item.startDate' (value) {
      if (value && this.item.endDate) {
        const startDate = startOfDay(utcToLocalTimezone(parse(value), this.item.timezoneName))
        const endDate = startOfDay(utcToLocalTimezone(parse(this.item.endDate), this.item.timezoneName))

        if (startDate > endDate) {
          this.onApplyChange('endDate', format(startDate, 'YYYY-MM-DD'))
        }
      }
    }
  },
  mounted () {
    this.timer = window.setInterval(() => {
      this.tableWidth = this.$el.offsetWidth
    }, 100)
  },
  destroyed () {
    window.clearInterval(this.timer)
  },
  methods: {
    formatDate (value) {
      return utcToLocalTimezone(parse(value), this.item.timezoneName, 'YYYY-MM-DD')
    },
    toggleSubscribeWeeklyReport () {
      if (this.isEditMode) {
        const value = !this.item.isSubscribeWeeklyReport
        this.onApplyChange('isSubscribeWeeklyReport', value)
      } else {
        this.$emit('onToggleSubscribeWeeklyReport', this.item)
      }
    },
    onCheckAllClick (category) {
      const hasChecked = this.hasCheckedApplies(category)

      for (const item of category.applies) {
        if (item.deleted) {
          continue
        }
        this.onApplyMetaChange('isChecked', !hasChecked)
      }
    },
    hasCheckedApplies (category) {
      return category.applies.filter(item => item.meta.isChecked).length
    },
    onDeleteClick () {
      openDialog({
        customData: {
          type: 'confirm',
          iconClass: 'icon--dialog-trash',
          title: this.$t('dialog.dashboard.deleteApply.title'),
          description: this.$t('dialog.dashboard.deleteApply.desc')
        },
        yes: this.$t('common.delete')
      }).then(result => {
        if (result) this.onApplyChange('deleted', true)
      })
    },
    onCancelDelete () {
      this.onApplyChange('deleted', false)
    },
    onCategoryNameChange (category, name) {
      this.$emit('onCategoryNameChange', category, name)
    },
    onApplyChange (key, value) {
      console.log(key, value)
      if (key === 'startDate' && value) {
        value = startOfDay(value)
      }
      if (key === 'endDate' && value) {
        value = endOfDay(value)
      }
      this.$emit('onApplyChange', {
        apply: this.item,
        key,
        value
      })
    },
    onApplyMetaChange (key, value) {
      this.$emit('onApplyMetaChange', {
        meta: this.item.meta,
        key,
        value
      })
    },
    onViewApplyClick () {
      if (this.isEditMode || !this.item.meta.canViewReport) return
      let defaultRouterName = 'heatmap-report'
      if (this.isABPlan) {
        defaultRouterName = 'ab.list'
      }
      const to = this.$router.resolve({ name: defaultRouterName, params: { aid: this.item.id } })
      window.open(to.href, '_blank')
    },
    showDatePicker (dateType) {
      triggerClickEvent()
      const metaName = dateType === 'start' ? 'showStartDatePicker' : 'showEndDatePicker'
      this.onApplyMetaChange(metaName, true)
    },
    getToday () {
      return utcToLocalTimezone(new Date(), this.item.timezoneName)
    },
    showSettingOption (event) {
      if (this.isSharedCategory) return false
      this.hideSettingOption()
      event.target.nextSibling.style.display = 'inline-block'
    },
    hideSettingOption () {
      this.$emit('onHideSettingOption')
    },
    goEditPage (item) {
      this.$router.push({ name: 'page-edit', params: { aid: item.id } })
    },
    showShareRow () {
      this.hideSettingOption()
      this.$emit('onAllApplyMetaChange', 'isShowShareRow', false)
      this.onApplyMetaChange('isShowShareRow', true)
    }
  }
}
</script>

<style lang="scss" scoped>
.apply_item {
  height: 80px;
  text-align: center;
  border-top: 1px solid #ebebeb;
  box-sizing: border-box;
  font-weight: 500;

  &:nth-of-type(1) {
    border-top: none;
  }

  &:nth-last-of-type(1) {
    border-bottom: 1px solid #ebebeb;
  }

  /*&:not(.deleted):hover {*/
  /*  background-color: #f4f4f4;*/
  /*}*/

  &.checked:not(.deleted) {
    border: solid 1px #289eef;
    outline: solid 3px rgba(40, 158, 239, 0.18);
  }

  .align-left {
    text-align: left;
    padding-left: 10px;
    box-sizing: border-box;
  }
  .align-right {
    text-align: right;
    padding-right: 15px;
    box-sizing: border-box;
  }

  .data__page {
    &:not(.disabled) {
      cursor: pointer;
      &:hover {
        .page-name {
          color: #1199af !important;
        }
      }
    }

    .page-name {
      font-size: 14px;
      font-weight: 600;
      line-height: 1.5;
      color: #2f2f2f;
      vertical-align: middle;

      .icon--share-send {
        vertical-align: text-top;
        padding-right: 8px;
      }

      .icon--share-receive {
        padding-right: 8px;
      }

      .icon--ab-test-exist {
        vertical-align: text-top;
        padding-right: 8px;
      }

      .icon--real-time-logo {
        width: 38.5px;
        height: 16px;
        background-size: 38px 10px;
        flex-shrink: 0;
        padding-right: 8px;
        vertical-align: middle;
        margin-top: 2px;
      }
    }
    .page-url {
      margin-top: 10px;
      font-size: 13px;
      line-height: 1;
      color: #666666;
      vertical-align: text-bottom;
      .match-type {
        display: inline-block;
        margin-right: 8px;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.17;
        color: #ffffff;
        padding: 1px 5px;
        border-radius: 4px;
        background-color: #8d8d8d;
      }
      span:not(.match-type) {
        vertical-align: text-bottom;
      }
    }
  }

  .data__setting {
    position: relative;
    .button__setting {
      &:hover {
        background-color: rgba(0, 0, 0, 0.06);
      }
      border-radius: 50%;
      width: 32px;
      height: 32px;
      background-position: center;

      &.disabled {
        opacity: 0.2;
        pointer-events: none;
      }
    }

    .setting__option--wrap {
      position: absolute;
      top: 60px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1;

      ul {
        width: 75px;
        border-radius: 6px;
        box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.25);
        background-color: #ffffff;
        overflow: hidden;

        li {
          cursor: pointer;
          height: 37px;
          line-height: 35px;
          font-size: 14px;
          color: #444444;

          &:hover {
            background-color: #ebebeb;
          }
        }
      }
    }
  }

  .data__pv {
    color: #2f2f2f;
  }
  .data__status,
  /deep/ .selected-item span,
  /deep/ .item-name {
    font-weight: 600;
    &.run {
      color: $stateRunColor;
    }
    &.analyzing {
      color: $stateAnalyzingColor;
    }
    &.stop,
    &.tempStop {
      color: $stateStopColor;
    }
    &.uninstalled,
    &.error,
    &.endAsError {
      color: $stateUnInstalledColor;
    }
    &.scheduledRun {
      color: $stateReservationColor;
    }
    &.endByPvLimit {
      color: $stateEndColor;
    }
    &.endByAnalysisDate {
      color: $stateEndColor;
    }
  }

  .data__start-date {
    color: #666;
  }

  .data__end-date {
    color: #666;
  }

  .data__confirm {
    button {
      border: solid 1px #57ab00;
      color: #4a8500;
    }
  }

  .data__delete__page {
    .icon {
      width: 32px;
      height: 32px;
      background-position: center;
      border-radius: 100%;
      appearance: none;
      cursor: pointer;

      &:hover {
        @include hoverStyle;
      }
    }
  }

  &.edit-mode {
    &.deleted {
      .deleted__layer {
        background-color: rgba(0, 0, 0, 0.03);
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 80px;

        div {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #444;

          button {
            @include buttonTypeSmall;
            line-height: 32px;
            border-radius: 20px;
            border: solid 1px #bbbbbb;
            background-color: #ffffff;
            margin-left: 19px;
            cursor: pointer;

            &:hover {
              @include hoverStyle;
            }
          }

          .icon {
            vertical-align: sub;
            margin-right: 5px;
          }
        }
      }

      .data__page {
        p {
          opacity: 0.4;
        }
        input {
          border: none;
          padding: 0;
        }
      }

      .item__handle--wrap,
      .data__pv,
      .data__status,
      .data__tracking-code,
      .data__start-date,
      .data__end-date,
      .data__weekly-report {
        pointer-events: none;
        opacity: 0;
      }
    }

    .align-left {
      padding-left: 60px;
    }
  }

  .data__page {
    position: relative;
    .icon--drag {
      position: absolute;
      top: 32px;
      left: 8px;
    }

    .item__handle--wrap {
      height: 17px;
      position: absolute;
      top: 31px;
      left: 28px;

      .icon--dashboard-chkbox {
        appearance: none;
        margin: 0;
      }
    }

    .page-name input {
      width: 90%;
      height: 38px;
      border-radius: 6px;
      border: solid 1px #bbbbbb;
      background-color: #ffffff;
      box-sizing: border-box;
      padding-left: 12px;
      padding-right: 12px;
    }
  }

  .data__status {
    /deep/ .ui_select .referrer--wrap {
      font-size: 13px;
    }
    &.run /deep/ .ui_select .referrer--wrap .selected-referrer {
      color: $stateRunColor;
    }
    &.stop /deep/ .ui_select .referrer--wrap .selected-referrer {
      color: $stateStopColor;
    }
    &.error /deep/ .ui_select .referrer--wrap .selected-referrer {
      color: $stateUnInstalledColor;
    }
    &.uninstalled /deep/ .ui_select .referrer--wrap .selected-referrer {
      color: $stateUnInstalledColor;
    }
    &.endByPvLimit
      /deep/
      .ui_select
      .referrer--wrap
      .selected-referrer {
      color: $stateEndColor;
    }
    &.endByAnalysisDate
      /deep/
      .ui_select
      .referrer--wrap
      .selected-referrer {
      color: $stateEndColor;
    }
    &.reservation /deep/ .ui_select .referrer--wrap .selected-referrer {
      color: $stateReservationColor;
    }
  }

  .data__start-date,
  .data__end-date {
    &.read-only {
      pointer-events: none;
      .selectedDate {
        border: solid 1px #d7d7d7;
        background-color: #e6e6e6;
        color: #999999;
      }
    }
    .selectedDate {
      display: block;
      margin: 0 auto;
      width: 100px;
      height: 38px;
      line-height: 36px;
      border-radius: 6px;
      border: solid 1px #bbbbbb;
      background-color: #ffffff;
      padding-left: 12px;
      padding-right: 12px;
      box-sizing: border-box;
      font-size: 14px;
      color: #2f2f2f;
      cursor: pointer;
      white-space: nowrap;
    }
  }
}

// todo: style customization
/*.sortable-fallback,*/
/*.sortable-ghost,*/
/*.sortable-drag,*/
/*.sortable-chosen {*/
/*  border: 2px solid #289eef !important;*/
/*}*/
</style>
